import React, { useEffect, useMemo, useRef, useState } from 'react';
import { randomInt } from './liveDrawUtils';

const TIMINGS = {
  normal: { fastMin: 1600, decel: 4800, minInterval: 55, maxInterval: 340, hold: 450 },
  reduced: { fastMin: 250, decel: 900, minInterval: 120, maxInterval: 260, hold: 150 },
};

const pad = (s, len) => String(s ?? '').padStart(len, ' ');

/**
 * Slot-machine roll through REAL sold numbers.
 * - Spins fast until `winner` is known (and a minimum time has passed)
 * - Then decelerates, locking reels left → right; unlocked reels keep showing
 *   real numbers that share the already-locked prefix
 * - Lands exactly on `winner`, then calls onDone()
 */
export function useSlotRoll({ active, pool, winner, reducedMotion, onDone }) {
  const len = useMemo(() => {
    const lens = (pool || []).map(n => String(n).length);
    if (winner) lens.push(String(winner).length);
    return Math.max(1, ...lens);
  }, [pool, winner]);

  const padded = useMemo(() => (pool || []).map(n => pad(n, len)), [pool, len]);
  const [view, setView] = useState({
    chars: Array(len).fill(' '),
    locked: 0,
    tick: 0,
    done: false,
  });

  const winnerRef = useRef(winner);
  winnerRef.current = winner;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!active) return undefined;
    const T = reducedMotion ? TIMINGS.reduced : TIMINGS.normal;
    const start = performance.now();
    let decelStart = null;
    let lastTick = 0;
    let tick = 0;
    let current = padded.length ? padded[randomInt(padded.length)] : pad('', len);
    let lockedCache = { locked: -1, candidates: padded };
    let raf = 0;
    let doneTimer = 0;
    let finished = false;
    setView({ chars: current.split(''), locked: 0, tick: 0, done: false });

    const candidatesFor = (locked, target) => {
      if (lockedCache.locked === locked) return lockedCache.candidates;
      const prefix = target ? target.slice(0, locked) : '';
      const candidates = locked === 0 ? padded : padded.filter(n => n.startsWith(prefix));
      lockedCache = { locked, candidates };
      return candidates;
    };

    const frame = now => {
      const elapsed = now - start;
      const target = winnerRef.current ? pad(winnerRef.current, len) : null;
      if (target && decelStart === null && elapsed >= T.fastMin) decelStart = now;

      let progress = 0;
      let interval = T.minInterval;
      if (decelStart !== null) {
        progress = Math.min(1, (now - decelStart) / T.decel);
        interval = T.minInterval + (T.maxInterval - T.minInterval) * progress * progress;
      }

      // Reel i locks at progress 0.3 + 0.7 * (i + 1) / len  (last one exactly at the end)
      let locked = 0;
      if (target) {
        for (let i = 0; i < len; i++) {
          if (progress >= 0.3 + (0.7 * (i + 1)) / len - 1e-9) locked = i + 1;
        }
      }

      if (progress >= 1 && target) {
        if (!finished) {
          finished = true;
          setView({ chars: target.split(''), locked: len, tick: tick + 1, done: true });
          doneTimer = window.setTimeout(() => onDoneRef.current && onDoneRef.current(), T.hold);
        }
        return;
      }

      if (now - lastTick >= interval) {
        lastTick = now;
        tick += 1;
        const candidates = candidatesFor(locked, target);
        let next;
        if (candidates.length >= 3) {
          do {
            next = candidates[randomInt(candidates.length)];
          } while (next === current && candidates.length > 1);
        } else {
          // Too few real numbers left for this prefix: spin unlocked reels with digits
          next = (target || current)
            .split('')
            .map((c, i) => (i < locked ? c : c === ' ' ? ' ' : String(randomInt(10))))
            .join('');
        }
        if (target && locked > 0) next = target.slice(0, locked) + next.slice(locked);
        current = next;
        setView({ chars: current.split(''), locked, tick, done: false });
      }
      raf = requestAnimationFrame(frame);
    };

    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(doneTimer);
    };
  }, [active]);

  return { ...view, len };
}

/**
 * Visual reels. `chars` may contain ' ' for padding (rendered empty).
 * `winnerMode` paints all reels gold (after reveal).
 */
export function SlotReels({
  chars,
  locked = 0,
  tick = 0,
  winnerMode = false,
  fontSize,
  idle = false,
}) {
  return (
    <div
      className='flex items-center justify-center'
      style={{ gap: '0.12em', fontSize }}
      aria-live='polite'
      aria-atomic='true'
    >
      {chars.map((c, i) => {
        const isLocked = winnerMode || i < locked;
        const cls = ['sev-reel', isLocked ? 'sev-locked' : '', winnerMode ? 'sev-winner' : '']
          .filter(Boolean)
          .join(' ');
        const content = c === ' ' ? ' ' : c;
        return (
          <div key={i} className={cls}>
            {idle ? (
              <span style={{ opacity: 0.35 }}>{content}</span>
            ) : isLocked ? (
              <span key='lock' className='sev-digit-lock'>
                {content}
              </span>
            ) : (
              <span key={tick} className='sev-digit-roll'>
                {content}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
